import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Round avatar with hover-to-upload + remove. Reads file, downsamples to 256×256 in a
 * canvas (keeps payloads tiny), then POSTs the data URI to /api/players/me/avatar.
 */
export default function PlayerAvatarUploader({ avatarUrl, name, onChange, size = 96 }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const initial = (name || "?").trim().charAt(0).toUpperCase();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setBusy(true);
    try {
      const dataUri = await new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => { img.src = ev.target.result; };
        reader.onerror = reject;
        img.onload = () => {
          const TARGET = 256;
          const c = document.createElement("canvas");
          c.width = TARGET; c.height = TARGET;
          const ctx = c.getContext("2d");
          const ratio = Math.min(img.width, img.height);
          const sx = (img.width - ratio) / 2;
          const sy = (img.height - ratio) / 2;
          ctx.drawImage(img, sx, sy, ratio, ratio, 0, 0, TARGET, TARGET);
          resolve(c.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post("/players/me/avatar", { avatar_url: dataUri });
      onChange?.(data.avatar_url);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/players/me/avatar");
      onChange?.(null);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block group" style={{ width: size, height: size }} data-testid="avatar-uploader">
      <div
        className="rounded-full overflow-hidden border-2 ring-2 ring-black/40"
        style={{ width: size, height: size, borderColor: "var(--brand-secondary)", backgroundColor: "var(--ink-2)" }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={name || "Player"} className="w-full h-full object-cover" referrerPolicy="no-referrer" data-testid="avatar-image" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display font-black text-white" style={{ fontSize: size / 2.3 }} data-testid="avatar-initial">
            {initial}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-white shadow-lg ring-2 ring-[var(--ink-1)]"
        style={{ backgroundColor: "var(--brand-secondary)" }}
        title="Change profile photo"
        data-testid="avatar-upload-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
      </button>
      {avatarUrl && !busy && (
        <button
          type="button"
          onClick={remove}
          className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-black flex items-center justify-center text-white/80 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove photo"
          data-testid="avatar-remove-btn"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="avatar-file-input" />
    </div>
  );
}
